import type { SuntraceQuality, SuntraceQualityPreference } from "./types";

const qualityRank: Record<SuntraceQuality, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

const qualityFromRank = (rank: number): SuntraceQuality =>
  rank >= 2 ? "high" : rank <= 0 ? "low" : "medium";

const detectAutomaticQuality = (): SuntraceQuality => {
  const hardware = navigator as Navigator & { deviceMemory?: number };
  const memory = hardware.deviceMemory ?? 6;
  const cores = hardware.hardwareConcurrency;
  const mobileWidth = Math.min(window.screen.width, window.screen.height) < 600;
  if (mobileWidth || memory <= 4 || cores <= 4) {
    return "medium";
  }
  if (memory >= 8 && cores >= 8) {
    return "high";
  }
  return "medium";
};

export type SuntraceQualityManager = {
  quality: SuntraceQuality;
  averageFrameTime: number;
  preference: SuntraceQualityPreference;
  sample: (deltaSeconds: number) => SuntraceQuality | null;
  setPreference: (preference: SuntraceQualityPreference) => SuntraceQuality;
};

export const createSuntraceQualityManager = (
  initialPreference: SuntraceQualityPreference,
  adaptive: boolean,
): SuntraceQualityManager => {
  let preference = initialPreference;
  let quality = preference === "auto" ? detectAutomaticQuality() : preference;
  let averageFrameTime = 16.7;
  let slowFrames = 0;
  let fastFrames = 0;
  let cooldownFrames = 180;
  const manager: SuntraceQualityManager = {
    get quality() {
      return quality;
    },
    get averageFrameTime() {
      return averageFrameTime;
    },
    get preference() {
      return preference;
    },
    sample(deltaSeconds) {
      const frameTime = Math.min(80, Math.max(1, deltaSeconds * 1_000));
      averageFrameTime = averageFrameTime * 0.96 + frameTime * 0.04;
      if (!adaptive || preference !== "auto") {
        return null;
      }
      cooldownFrames = Math.max(0, cooldownFrames - 1);
      slowFrames = averageFrameTime > (quality === "high" ? 21.5 : 25) ? slowFrames + 1 : 0;
      fastFrames = averageFrameTime < 14.2 ? fastFrames + 1 : 0;
      if (cooldownFrames > 0) {
        return null;
      }
      const currentRank = qualityRank[quality];
      if (slowFrames >= 120 && currentRank > 0) {
        quality = qualityFromRank(currentRank - 1);
        slowFrames = 0;
        fastFrames = 0;
        cooldownFrames = 360;
        return quality;
      }
      if (fastFrames >= 540 && currentRank < 2) {
        quality = qualityFromRank(currentRank + 1);
        slowFrames = 0;
        fastFrames = 0;
        cooldownFrames = 600;
        return quality;
      }
      return null;
    },
    setPreference(nextPreference) {
      preference = nextPreference;
      quality = preference === "auto" ? detectAutomaticQuality() : preference;
      slowFrames = 0;
      fastFrames = 0;
      cooldownFrames = 240;
      return quality;
    },
  };
  return manager;
};
